import { useQuery } from '@tanstack/react-query';
import cacheQueryKeys from 'resources/ReactQuery/queryCacheKeys';
import { statusCodesMapper } from 'utils/exception/statusCodesMapper';

import { useAppMessages } from 'components/Common/hooks/useAppMessages';
import { useToastController } from 'components/Common/Toast';

import { fetchAvailableServices } from '../api/api';
import { AvailableServicesResponse, CategoryType, IndustryType } from '../types/types';

const sortByOrder = (prevElement: IndustryType, nextElement: IndustryType) =>
  prevElement.orderIndex - nextElement.orderIndex;

const assignServicesToCategory =
  (data: AvailableServicesResponse) =>
  (category: CategoryType): Required<CategoryType> => {
    const services = data.services.filter((service) => service.categoryUuid === category.uuid).sort(sortByOrder);

    return {
      ...category,
      services,
    };
  };

export const formatServicesData = (data: AvailableServicesResponse | null) => {
  if (!data) {
    return [];
  }
  const sortedIndustries = data.industries.sort(sortByOrder);

  return sortedIndustries.map((industry) => {
    const categories = data.categories
      .filter((category) => category.industryUuid === industry.uuid)
      .sort(sortByOrder)
      .map(assignServicesToCategory(data));
    return {
      ...industry,
      categories,
    };
  });
};

export const useServices = () => {
  const { showToast } = useToastController();
  const { message } = useAppMessages();

  return useQuery([cacheQueryKeys.AvailableServices], fetchAvailableServices, {
    select: (response) => {
      return statusCodesMapper(response, {
        success: (data) => {
          return {
            data: formatServicesData(data),
            ...data,
          };
        },
        unauthenticated: () => {
          window.location.reload();
        },
        unhandled: () => {
          showToast(message.error.unknown);
        },
      });
    },
    staleTime: 0,
  });
};
